import { useEffect, useState } from "react";
import { convertToPreferredUnitBySensorType, roundToDecimalPlaces, UnitPreferences } from "verdi-common";
import { generateUID } from "verdi-common/utils/index";
import { MasterIndex } from "verdi-web-client";
import { IrrigationDeviceBase } from "verdi-web-client/Models/Devices/IrrigationDeviceBase";
import { IrrigationDeviceSensor } from "verdi-web-client/Models/Devices/IrrigationDeviceSensor";
import { useUnitPreferences } from "@/hooks/use-unit-preferences";
import { ALL_DATA_TYPES } from "@/services/data-types";

const DATA_TYPES_TO_DISPLAY = new Set(["v1transducer", "v1flowmeter"]);

type SensorReading = {
    date?: Date;
    value?: number;
    dataType?: string;
    units?: string;
    labelTranslationKey?: string;
};

export type SensorReadingRange = Omit<SensorReading, "value"> & {
    minValue: number;
    maxValue: number;
    shouldDisplayAsSingleSensorReading: boolean;
};

function getZoneDevices(zoneId: string) {
    const zone = MasterIndex.zone.byID[zoneId];
    if (!zone || !Object.prototype.hasOwnProperty.call(zone, "devices")) {
        return undefined;
    }

    return MasterIndex.zone.byID[zoneId].devices;
}

function getConnectedSensorReadings(device: IrrigationDeviceBase, unitPreferences: UnitPreferences): SensorReading[] {
    const connectedSensorReadings = device.connectedSensors
        // Filter out sensors that are not of a data type we want to display
        .filter(
            (sensor): sensor is IrrigationDeviceSensor =>
                sensor !== undefined && DATA_TYPES_TO_DISPLAY.has(sensor.dataType ?? ""),
        )
        // Filter out sensors with missing readings
        .filter((sensor) => sensor.lastReading !== undefined)
        .map((sensor) => {
            const reading = sensor.lastReading!;
            const { value: convertedValue, unitConfiguration } = convertToPreferredUnitBySensorType({
                value: reading.value,
                sensorType: sensor.sensorType,
                unitPreferences,
            });

            return {
                dataType: reading.dataType,
                units: unitConfiguration.symbol,
                value: convertedValue,
                date: reading.date,
                labelTranslationKey:
                    ALL_DATA_TYPES[reading.dataType as keyof typeof ALL_DATA_TYPES]?.labelTranslationKey,
            };
        });

    return connectedSensorReadings;
}

const computeZoneSensorReadings = (zoneId: string, unitPreferences: UnitPreferences) => {
    const devices = getZoneDevices(zoneId);

    if (!devices) {
        return undefined;
    }

    const connectedSensorReadings = devices
        .filter((device): device is IrrigationDeviceBase => device instanceof IrrigationDeviceBase)
        .map((device) => getConnectedSensorReadings(device, unitPreferences));

    // Flatten all device readings so one reduce can compute cross-sensor min/max per data type
    const allReadings = connectedSensorReadings.flat();

    // Compose into a record of data type to sensor reading range, with min and max values
    const { ranges } = allReadings.reduce<{
        ranges: Record<string, SensorReadingRange>;
        counts: Record<string, number>;
    }>(
        (acc, reading) => {
            const { dataType, units, labelTranslationKey } = reading;
            if (dataType == null) return acc;
            const existing = acc.ranges[dataType];
            const value = reading.value ?? 0;
            const minValue = roundToDecimalPlaces(Math.min(existing?.minValue ?? Infinity, value));
            const maxValue = roundToDecimalPlaces(Math.max(existing?.maxValue ?? -Infinity, value));
            const count = (acc.counts[dataType] ?? 0) + 1;

            // Use the most recent date across devices so "time ago" reflects data freshness
            const existingTime = existing?.date != null ? new Date(existing.date).getTime() : -Infinity;
            const currentTime = reading.date != null ? new Date(reading.date).getTime() : -Infinity;
            const mostRecentDate = currentTime >= existingTime ? reading.date : existing?.date;

            // Display as single item when there is only one reading, or multiple readings with the same value
            const shouldDisplayAsSingleSensorReading = count === 1 || minValue === maxValue;

            return {
                ranges: {
                    ...acc.ranges,
                    [dataType]: {
                        dataType,
                        units,
                        labelTranslationKey,
                        minValue,
                        maxValue,
                        date: mostRecentDate,
                        shouldDisplayAsSingleSensorReading,
                    },
                },
                counts: { ...acc.counts, [dataType]: count },
            };
        },
        { ranges: {}, counts: {} },
    );

    return ranges;
};

// TODO add polling for all devices in the zone at a higher frequency than the default polling frequency
export const useZoneSensorReadings = (zoneId: string) => {
    const { unitPreferences, isLoading: isLoadingUnitPreferences } = useUnitPreferences();

    const [sensorReadingsByDataType, setSensorReadingsByDataType] = useState<Record<string, SensorReadingRange>>();

    useEffect(() => {
        if (isLoadingUnitPreferences) {
            // Clear stale readings from the previous org while unit preferences reload
            setSensorReadingsByDataType(undefined);
            return undefined;
        }

        const initialSensorReadingsByDataType = computeZoneSensorReadings(zoneId, unitPreferences);
        setSensorReadingsByDataType(initialSensorReadingsByDataType);

        // Attach event listeners to all connected devices in the zone
        const devices = getZoneDevices(zoneId) ?? [];
        const listenerIds: Record<string, string> = {};
        devices.forEach((device) => {
            /**
             * For simplicity and time constraint reasons, we decided we will discard all non-first party devices for now.
             * This will eventually be replaced by DataStreams and we will no need this constraint
             */
            if (device instanceof IrrigationDeviceBase) {
                const listenerId = generateUID();
                device.onChange.addListener(() => {
                    // When any device in the zone changes, we need to recompute the sensor readings
                    const newConnectedSensorReadings = computeZoneSensorReadings(zoneId, unitPreferences);
                    setSensorReadingsByDataType(newConnectedSensorReadings);
                }, listenerId);
                listenerIds[device.id] = listenerId;
            }
        });

        return () => {
            devices.forEach((device) => {
                if (device instanceof IrrigationDeviceBase && listenerIds[device.id]) {
                    device.onChange.removeListener(listenerIds[device.id]);
                }
            });
        };
    }, [zoneId, unitPreferences, isLoadingUnitPreferences]);

    return { sensorReadingsByDataType, isLoading: isLoadingUnitPreferences };
};
